/**
 * @jsx jsx
 */
import React, { useCallback } from 'react';
import { Box, Pressable } from '@atlaskit/primitives';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { TriggerProps } from '@atlaskit/popup';
import { useIntl } from '@atlassian/jira-intl';
import type { CustomTriggerContext } from '@atlassian/jira-issue-links-stats/src/ui/issue-links-stats-button/issue-links-details-popup-entrypoint-container/types.tsx';
import { messages } from '@atlassian/jira-issue-links-stats/src/ui/issue-links-stats-button/issue-links-details-popup-entrypoint-container/messages.tsx';

const styles = cssMap({
	pressable: {
		font: token('font.body.small'),
		paddingTop: token('space.0'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.050'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.xsmall'),
		color: token('color.text'),
		display: 'inline-flex',
		alignItems: 'center',
		verticalAlign: 'text-bottom',
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.gray.subtlest.pressed'),
		},
		transition: 'background-color 0.3s ease, transform 0.3s ease',
	},
	pressableOpen: {
		outlineWidth: token('border.width.focused'),
		outlineStyle: 'solid',
		outlineOffset: token('space.025'),
		outlineColor: token('color.border.focused'),
		borderRadius: token('radius.xsmall'),
	},
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
});

export const CustomIssueLinksStatsTrigger = (
	popupTriggerProps: TriggerProps,
	context: CustomTriggerContext,
) => {
	const { formatMessage } = useIntl();

	const {
		isOpen,
		issueLinkCount,
		handleButtonClicked,
		issueKey,
		iconLabel,
		triggerRef,
		openTriggerRef,
		updateTriggerRefOnIssueLinksStatsClose,
		linkedIssuesRef,
		stopEventPropagation,
	} = context;

	const handleRef = useCallback(
		(element: HTMLElement | null) => {
			triggerRef(element);
			openTriggerRef(element);
			updateTriggerRefOnIssueLinksStatsClose(element);
			linkedIssuesRef.current = element;

			// Use type guard to safely handle ref that can be either RefCallback or RefObject
			if (typeof popupTriggerProps.ref === 'function') {
				popupTriggerProps.ref(element);
			}
		},
		[
			triggerRef,
			openTriggerRef,
			updateTriggerRefOnIssueLinksStatsClose,
			linkedIssuesRef,
			popupTriggerProps,
		],
	);

	const handleOnMouseDown = useCallback((event: React.SyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
	}, []);

	return (
		<Pressable
			{...popupTriggerProps}
			testId={`issue-links-stats.ui.issue-links-stats-button.custom-theme-button-${issueKey}`}
			ref={handleRef}
			onClick={handleButtonClicked}
			onContextMenu={stopEventPropagation}
			onKeyDown={stopEventPropagation}
			onMouseDown={handleOnMouseDown}
			aria-label={formatMessage(messages.issueLinksButtonIssueTermRefresh, { issueLinkCount })}
			xcss={cx(styles.pressable, isOpen && styles.pressableOpen)}
		>
			<Box as="span" xcss={styles.wrapper}>
				<WorkItemIcon label={iconLabel} size="small" />
				{issueLinkCount}
			</Box>
		</Pressable>
	);
};
